import { Dialog } from '@syncfusion/ej2-popups';

import { escapeHtml } from '@/shared/utils/misc';

export type LegacyPrimaryContactMatch = {
  email: string;
  emailToCopy: string;
  id: string;
  name: string;
  phoneToCopy: string;
};

export type LegacyPrimaryContactModalResult =
  | { action: 'create' }
  | { action: 'link'; individualId: string; copyEmail: boolean; copyPhone: boolean }
  | null;

let dialog: Dialog | null = null;
let resolvePromise: ((value: LegacyPrimaryContactModalResult) => void) | null = null;
let currentMatches: LegacyPrimaryContactMatch[] = [];

const settle = (value: LegacyPrimaryContactModalResult) => {
  resolvePromise?.(value);
  resolvePromise = null;
};

const MATCH_RADIO_NAME = 'legacyPrimaryContactMatch';
const COPY_OPTIONS_ID = 'legacyPrimaryContactCopyOptions';
const COPY_EMAIL_ID = 'legacyPrimaryContactCopyEmail';
const COPY_PHONE_ID = 'legacyPrimaryContactCopyPhone';

const copyCheckbox = (id: string, label: string, value: string) =>
  value
    ? `
    <div class="form-check mb-0">
      <input class="form-check-input" type="checkbox" id="${id}" checked />
      <label class="form-check-label" for="${id}">${label} (${escapeHtml(value)})</label>
    </div>`
    : '';

const buildContent = (matches: LegacyPrimaryContactMatch[]) => `
  <div class="d-flex flex-column gap-3">
    <div class="alert alert-warning mb-0 small" role="alert">
      ${matches.length === 1 ? 'A Linked Individual matches' : 'Linked Individuals match'} the legacy Primary Contact.
      You can make ${matches.length === 1 ? 'them' : 'one of them'} the Primary Contact instead of creating a new Individual.
    </div>
    <div class="d-flex flex-column gap-2">
      ${matches
        .map(
          (match, index) => `
        <div class="form-check mb-0">
          <input class="form-check-input" type="radio" name="${MATCH_RADIO_NAME}" id="${MATCH_RADIO_NAME}${index}" value="${escapeHtml(match.id)}"${index === 0 ? ' checked' : ''} />
          <label class="form-check-label" for="${MATCH_RADIO_NAME}${index}">
            ${escapeHtml(match.name)}
            ${match.email ? `<span class="text-muted fs-7">${escapeHtml(match.email)}</span>` : ''}
          </label>
        </div>`,
        )
        .join('')}
    </div>
    <div id="${COPY_OPTIONS_ID}" class="d-flex flex-column gap-2"></div>
  </div>
`;

const selectedIndividualId = () =>
  document.querySelector<HTMLInputElement>(`#legacyPrimaryContactDialog input[name="${MATCH_RADIO_NAME}"]:checked`)
    ?.value;

const isChecked = (id: string) => !!document.querySelector<HTMLInputElement>(`#${id}`)?.checked;

// only offer to copy legacy details the selected Individual is missing
const refreshCopyOptions = () => {
  const container = document.getElementById(COPY_OPTIONS_ID);

  if (!container) return;

  const match = currentMatches.find(({ id }) => id === selectedIndividualId());

  container.innerHTML = match
    ? copyCheckbox(COPY_EMAIL_ID, 'Copy email', match.emailToCopy) +
      copyCheckbox(COPY_PHONE_ID, 'Copy phone', match.phoneToCopy)
    : '';
  container.classList.toggle('d-none', !container.innerHTML);
};

const ensureDialog = () => {
  if (dialog) {
    return dialog;
  }

  const host = document.createElement('div');
  host.id = 'legacyPrimaryContactDialog';
  host.addEventListener('change', (e) => {
    if ((e.target as HTMLInputElement).name === MATCH_RADIO_NAME) refreshCopyOptions();
  });
  document.body.appendChild(host);

  dialog = new Dialog({
    header: 'A matching Individual is already linked',
    content: '',
    target: document.body,
    showCloseIcon: true,
    isModal: true,
    visible: false,
    width: '560px',
    zIndex: 1035,
    animationSettings: { effect: 'Fade' },
    buttons: [
      {
        click: () => {
          dialog?.hide();
          settle(null);
        },
        buttonModel: { content: 'Cancel', cssClass: 'e-btn e-flat' },
      },
      {
        click: () => {
          settle({ action: 'create' });
          dialog?.hide();
        },
        buttonModel: { content: 'Create new anyway', cssClass: 'e-btn e-flat' },
      },
      {
        click: () => {
          const individualId = selectedIndividualId();
          if (!individualId) return;

          settle({
            action: 'link',
            individualId,
            copyEmail: isChecked(COPY_EMAIL_ID),
            copyPhone: isChecked(COPY_PHONE_ID),
          });
          dialog?.hide();
        },
        buttonModel: { content: 'Use selected', cssClass: 'e-btn e-primary', isPrimary: true },
      },
    ],
    close: () => settle(null),
  });

  dialog.appendTo(host);
  return dialog;
};

export const openLegacyPrimaryContactModal = (
  matches: LegacyPrimaryContactMatch[],
): Promise<LegacyPrimaryContactModalResult> =>
  new Promise((resolve) => {
    resolvePromise = resolve;
    currentMatches = matches;

    const dlg = ensureDialog();

    dlg.content = buildContent(matches);
    dlg.dataBind();
    refreshCopyOptions();
    dlg.show();
  });
